import type { RecurrenceOverride, RecurrenceRule, Task } from "@app/core";
import { addDays, endOfDay, matrixEnabledFor, startOfDay } from "@app/core";
import { useMemo, useRef, useState } from "react";

import { useI18n } from "../../lib/useI18n.js";
import { toast } from "../../stores/toast.store.js";
import { beginUndoable, endUndoable, undoAction } from "../../stores/undo.js";
import { useSettingsStore, useTasksStore } from "../../stores/index.js";
import { type NextInstanceCache, tasksForList } from "../../routes/tasks/next-instances.js";
import { expandTasksWindowed, type RuleExpansionCache } from "../calendar-view/build-days.js";
import { buildMatrixView } from "../matrix-view/model.js";
import { planCandidates, planDay, type DayPlan, type PlanSlot } from "../matrix-view/plan-day.js";
import { useEffectiveMatrixDisplay } from "../priority-matrix/useEffectiveMatrixDisplay.js";
import { isOverdue, OVERDUE_LOOKBACK_DAYS } from "./agenda.js";

interface TodayPlanInput {
  /** Calendar-visible + Today-exclusion-filtered tasks — availability must not
   *  forget a commitment merely because an interactive filter hides it. */
  availabilityTasks: Task[];
  /** The same list with the interactive view filters applied — candidates. */
  filteredTasks: Task[];
  rules: Record<string, RecurrenceRule>;
  overrides: Record<string, RecurrenceOverride[]>;
  now: Date;
}

/**
 * "Plan my day" for the Today screen — the exact pipeline the Priorities load
 * strip uses (decision-ordered candidates from `buildMatrixView`, busy time
 * from recurrence-expanded visible tasks, first-fit via `planDay`), bound to
 * today. Applying wraps every reschedule in ONE undoable scope, so a whole
 * plan reverts with a single Undo.
 */
export function useTodayPlan({ availabilityTasks, filteredTasks, rules, overrides, now }: TodayPlanInput) {
  const edit = useTasksStore((s) => s.edit);
  const matrixScope = useSettingsStore((s) => s.matrixScope);
  const morningHour = useSettingsStore((s) => s.snoozeMorningHour);
  const eveningHour = useSettingsStore((s) => s.snoozeEveningHour);
  const defaultDurationMin = useSettingsStore((s) => s.defaultDurationMin);
  const displayOf = useEffectiveMatrixDisplay();
  const { t } = useI18n();
  const [planOpen, setPlanOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const applyingRef = useRef(false);

  const nextCache = useRef<NextInstanceCache>(new Map());
  const missedCache = useRef<RuleExpansionCache>(new Map());
  const busyCache = useRef<RuleExpansionCache>(new Map());

  const plan: DayPlan | null = useMemo(() => {
    if (!planOpen) return null;
    const dayStart = startOfDay(now);
    // Candidates come from the full-range decision order (board/queue rules):
    // one entry per series, plus recent missed occurrences for overdue pull-in.
    const collapsed = tasksForList(filteredTasks, rules, overrides, now, nextCache.current, false);
    const recurring = filteredTasks.filter((task) => task.recurrence_id && !task.is_event);
    const missed = recurring.length
      ? expandTasksWindowed(
          recurring, rules, overrides, startOfDay(addDays(now, -OVERDUE_LOOKBACK_DAYS)), now, missedCache.current,
        ).filter((task) => task.virtual && isOverdue(task, now))
      : [];
    const full = buildMatrixView(
      [...collapsed, ...missed],
      { horizon: "all", includeOverdue: true, includeUndated: true, includeDone: false, dayFocus: null, hidingActive: matrixScope !== "off" },
      now,
      displayOf,
      (task) => matrixEnabledFor(matrixScope, task.is_event),
    );
    const candidates = planCandidates(full.queue.map((i) => i.task), dayStart, now);
    const candidateIds = new Set(candidates.map((task) => task.id));
    const busy = expandTasksWindowed(
      availabilityTasks, rules, overrides, dayStart, endOfDay(now), busyCache.current, true,
    ).filter((task) => !candidateIds.has(task.id));
    return planDay(
      candidates, busy, dayStart,
      { startHour: morningHour, endHour: eveningHour, fallbackMinutes: defaultDurationMin },
      now,
    );
  }, [
    planOpen, filteredTasks, availabilityTasks, rules, overrides, now,
    displayOf, matrixScope, morningHour, eveningHour, defaultDurationMin,
  ]);

  async function applyPlan(slots: PlanSlot[]) {
    if (applyingRef.current) return;
    applyingRef.current = true;
    setApplying(true);
    setPlanOpen(false);
    let enteredUndo = false;
    let outer = false;
    try {
      enteredUndo = true;
      outer = await beginUndoable("Plan day");
      for (const s of slots) {
        await edit(s.task.id, {
          due_at: s.start.toISOString(),
          all_day: false,
          duration_minutes: s.minutes,
        });
      }
      if (outer) toast.withAction("success", t("priorities.plan.applied", { count: slots.length }), undoAction());
    } catch {
      setPlanOpen(true);
      if (outer) toast.withAction("error", t("priorities.plan.failed"), undoAction());
      else toast.error(t("priorities.plan.failed"));
    } finally {
      if (enteredUndo) endUndoable();
      applyingRef.current = false;
      setApplying(false);
    }
  }

  return {
    planOpen,
    applying,
    openPlan: () => { if (!applyingRef.current) setPlanOpen(true); },
    closePlan: () => setPlanOpen(false),
    plan,
    applyPlan,
  };
}
